import {ThankYouPageStore} from '../../domain/stores/ThankYouPageStore';
import {BaseController} from '@wix/wixstores-client-storefront-sdk/dist/es/src/viewer-script/controller-factory/BaseController';
import {SiteStore} from '@wix/wixstores-client-storefront-sdk/dist/es/src/viewer-script/site-store/SiteStore';
import {DataExtensionsService} from '@wix/wixstores-client-storefront-sdk';
import {ControllerParams} from '@wix/yoshi-flow-editor';
import {ShippingService} from '../../domain/services/ShippingService';
import {NavigationService} from '../../domain/services/NavigationService';
import {NavigationStore} from '../../domain/stores/NavigationStore';
import {BIService} from '../../domain/services/BIService';
import {OrderService} from '../../domain/services/OrderService';
import {wixcodePublicApi} from './WixCode/wixcodePublicApi';
import {TrackEventService} from '../../domain/services/TrackEventService';
import {AddressTranslationService} from '../../domain/services/AddressTranslationService';
import {PaymentsService} from '../../domain/services/PaymentsService';
import {CartService} from '../../domain/services/CartService';
import {CheckoutService} from '../../domain/services/CheckoutService';
import {navigateToHeadlessIfNeeded, NavigationType} from '@wix/wix-to-headless-redirect-client';
import {Specs} from './constants';

export class ThankYouPageController extends BaseController {
  protected readonly thankYouPageStore: ThankYouPageStore;
  protected readonly navigationStore: NavigationStore;
  protected readonly orderService: OrderService;
  protected readonly cartService: CartService;
  protected readonly checkoutService: CheckoutService;
  protected readonly biService: BIService;
  protected readonly trackEventService: TrackEventService;
  protected readonly navigationService: NavigationService;
  protected readonly dataExtensionsService: DataExtensionsService;

  constructor(controllerParams: ControllerParams) {
    super(controllerParams);
    const flowAPI = controllerParams.flowAPI;
    const siteStore: SiteStore = controllerParams.appData.context.siteStore;
    const addressTranslationService = new AddressTranslationService({siteStore});
    const paymentsService = new PaymentsService({siteStore});
    const orderService = new OrderService({siteStore, addressTranslationService});
    const shippingService = new ShippingService({flowAPI, orderService});
    const biService = new BIService({siteStore});
    const navigationService = new NavigationService({flowAPI, siteStore, biService, orderService});
    const trackEventService = new TrackEventService({siteStore, orderService});
    const dataExtensionsService = new DataExtensionsService({httpClient: flowAPI._httpClient});

    this.navigationStore = new NavigationStore({navigationService});
    this.thankYouPageStore = new ThankYouPageStore({
      biService,
      shippingService,
      orderService,
      paymentsService,
      siteStore,
      dataExtensionsService,
    });

    this.orderService = orderService;
    this.biService = biService;
    this.trackEventService = trackEventService;
    this.cartService = new CartService({siteStore});
    this.checkoutService = new CheckoutService({siteStore});
    this.navigationService = navigationService;
    this.dataExtensionsService = dataExtensionsService;
  }

  public readonly load = async () => {
    const {navigatedToHeadless} = navigateToHeadlessIfNeeded({
      navParams: {
        logicalName: NavigationType.ECOM_THANK_YOU_PAGE,
        params: {orderId: this.orderService.orderGuid},
      },
      location: this.siteStore.location,
    });
    if (navigatedToHeadless) {
      return this.setProps({skipRender: true});
    }
    const fetchDataExtensionsSchemaPromise = this.dataExtensionsService.fetchDataExtensionsSchema().catch((e) => {
      this.flowAPI.reportError ? this.flowAPI.reportError(e) : /* istanbul ignore next */ console.error(e);
    });
    try {
      await Promise.all([this.orderService.fetchOrder(), fetchDataExtensionsSchemaPromise]);
    } catch (e) {
      /* istanbul ignore else: todo: test */
      // @ts-expect-error
      if (this.siteStore.isSSR() && e.message.includes('PERMISSION_DENIED')) {
        return this.setProps({ssrError: true});
      }
      /* istanbul ignore next: ignore exception flow */
      throw e;
    }
    if (this.siteStore.experiments.enabled(Specs.SendMarkCheckoutAsCompletedWithOrderIdFromTYP)) {
      void this.checkoutService.markCheckoutAsCompleted(this.orderService.checkoutId, this.orderService.orderGuid);
    } else {
      void this.checkoutService.markCheckoutAsCompleted(this.orderService.checkoutId);
    }

    void (await this.cartService.clearCart(this.orderService.cartId));
    void this.reportOnLoad();

    this.setProps({
      thankYouPageStore: await this.thankYouPageStore.toProps(),
      navigationStore: await this.navigationStore.toProps(),
      fitToContentHeight: true,
    });
  };

  public readonly init = () => this.load();

  public readonly onStyleUpdate = (styleParams: {}) => {
    return styleParams;
  };

  /* istanbul ignore next */
  public readonly exports: any = () => {
    return wixcodePublicApi({siteStore: this.siteStore, panoramaClient: this.flowAPI.panoramaClient});
  };

  public getFreeTexts = (): string[] => [];

  public getOptionsOverrides() {
    return this.siteStore.experiments.enabled(Specs.UseGetSettingsWithDefaults)
      ? {}
      : {shouldCallAppSettingsOnlyWhenMultilingualIsNeeded: false};
  }

  private reportOnLoad() {
    if (this.siteStore.isSSR()) {
      return;
    }

    this.trackEventService.trackPurchase(this.orderService.trackPurchaseParams);

    const {paymentMethodType, itemType, numOfLineItems, concatenatedCatalogAppIds} =
      this.orderService.extractDataFromItemsForBI();
    const additionalFeesPrice = this.orderService.additionalFeesPrice;
    const additionalFeesNumber = this.orderService.additionalFees?.length;

    this.biService.thankYouPageLoadSf({
      orderId: this.orderService.orderGuid,
      cartId: this.orderService.cartId,
      checkoutId: this.orderService.checkoutId,
      shippingMethodType: this.orderService.deliveryType || '',
      cartType: this.orderService.cartType,
      paymentStatus: this.orderService.paymentStatus,
      paymentProvider: this.orderService.paymentMethod,
      numOfLineItems,
      itemType,
      paymentMethodType,
      concatenatedCatalogAppIds,
      additionalFeesPrice: additionalFeesPrice * 100000,
      additionalFeesNumber,
    });
  }
}
